// ANETT HORVATH — Public Pages
// Content-block driven public pages with full i18n support.

import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import ContentRenderer from '../../components/ContentRenderer';
import { useStore } from '../../store/database';
import { getBlockValue } from '../../store/content-blocks';
import type { SupportedLang } from '../../config/house.config';
import LanguageSwitcher from '../../components/LanguageSwitcher';

// Shared page shell
function PageShell({ children, showNav = true }: { children: React.ReactNode; showNav?: boolean }) {
  const { t } = useTranslation();
  const session = useStore(s => s.session);
  return (
    <div className="min-h-dvh bg-ivory flex flex-col">
      {showNav && (
        <nav className="flex items-center justify-between px-6 py-4 border-b border-charcoal/5">
          <Link to="/" className="font-serif text-xl tracking-widest-xl">AH</Link>
          <div className="flex items-center gap-4 text-[10px] tracking-widest uppercase font-sans text-charcoal/40">
            <Link to="/story" className="hover:text-charcoal transition-colors">{t('public.story')}</Link>
            <Link to="/product" className="hover:text-charcoal transition-colors">{t('public.product')}</Link>
            <Link to="/waitlist" className="hover:text-charcoal transition-colors">{t('public.enter')}</Link>
            {session ? (
              <Link to="/house" className="text-gold hover:text-gold/80 transition-colors">{t('public.house')}</Link>
            ) : (
              <Link to="/login" className="hover:text-charcoal transition-colors">{t('public.login')}</Link>
            )}
            <LanguageSwitcher />
          </div>
        </nav>
      )}
      <main className="flex-1">{children}</main>
      <footer className="px-6 py-8 border-t border-charcoal/5 text-center">
        <p className="text-[10px] tracking-widest uppercase text-charcoal/20 font-sans">
          {t('public.copyright', { year: new Date().getFullYear() })}
        </p>
      </footer>
    </div>
  );
}

// HOME / LANDING
export function HomePage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en') as SupportedLang;
  return (
    <PageShell>
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <ContentRenderer blockKey="hero_image" imageClass="w-full h-full object-cover opacity-60" />
        </div>
        <div className="relative z-10 text-center space-y-6 px-6">
          <ContentRenderer blockKey="hero_title" as="h1" className="font-serif text-4xl md:text-6xl tracking-widest-xl text-charcoal" />
          <ContentRenderer blockKey="hero_subtitle" as="p" className="text-[11px] tracking-widest-2xl uppercase text-charcoal/50 font-sans max-w-md mx-auto" />
          <Link to="/waitlist" className="inline-block px-8 py-3 bg-charcoal text-ivory text-[11px] tracking-widest-xl uppercase font-sans hover:bg-charcoal/90 transition-colors">
            {getBlockValue('hero_cta', lang) || t('public.enterTheHouse')}
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

// PRODUCT PAGE
export function ProductPage() {
  return (
    <PageShell>
      <div className="max-w-4xl mx-auto px-6 py-16 space-y-20">
        {/* AETHERA */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <ContentRenderer blockKey="product_aethera_image" imageClass="w-full aspect-[3/4] object-cover" />
          <div className="space-y-4">
            <ContentRenderer blockKey="product_aethera_title" as="h2" className="font-serif text-2xl tracking-widest" />
            <ContentRenderer blockKey="product_aethera_desc" as="p" className="text-sm font-sans text-charcoal/60 leading-relaxed" />
          </div>
        </section>
        {/* Moon of Life */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-4 md:order-2">
            <ContentRenderer blockKey="product_moonoflife_title" as="h2" className="font-serif text-2xl tracking-widest" />
            <ContentRenderer blockKey="product_moonoflife_desc" as="p" className="text-sm font-sans text-charcoal/60 leading-relaxed" />
          </div>
          <ContentRenderer blockKey="product_moonoflife_image" imageClass="w-full aspect-[3/4] object-cover md:order-1" />
        </section>
      </div>
    </PageShell>
  );
}

// STORY PAGE
export function StoryPage() {
  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-6 py-16 space-y-8">
        <ContentRenderer blockKey="story_title" as="h1" className="font-serif text-3xl tracking-widest text-center" />
        <ContentRenderer blockKey="story_image" imageClass="w-full aspect-[16/9] object-cover" />
        <ContentRenderer blockKey="story_body" as="p" className="text-sm font-sans text-charcoal/60 leading-relaxed max-w-xl mx-auto text-center" />
      </div>
    </PageShell>
  );
}

// WAITLIST PAGE
export function WaitlistPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <PageShell>
      <div className="max-w-sm mx-auto px-6 py-16 space-y-8 text-center">
        <ContentRenderer blockKey="waitlist_title" as="h1" className="font-serif text-3xl tracking-widest" />
        <ContentRenderer blockKey="waitlist_body" as="p" className="text-sm font-sans text-charcoal/60 leading-relaxed" />
        <button onClick={() => navigate('/register')} className="px-8 py-3 bg-charcoal text-ivory text-[11px] tracking-widest-xl uppercase font-sans hover:bg-charcoal/90 transition-colors">
          {t('public.requestEntry')}
        </button>
      </div>
    </PageShell>
  );
}

// ALLOCATION PAGE
export function AllocationPage() {
  const { t } = useTranslation();
  return (
    <PageShell>
      <div className="max-w-lg mx-auto px-6 py-16 space-y-8 text-center">
        <ContentRenderer blockKey="allocation_title" as="h1" className="font-serif text-3xl tracking-widest" />
        <ContentRenderer blockKey="allocation_body" as="p" className="text-sm font-sans text-charcoal/60 leading-relaxed" />
        <Link to="/releases" className="inline-block px-8 py-3 bg-charcoal text-ivory text-[11px] tracking-widest-xl uppercase font-sans hover:bg-charcoal/90 transition-colors">
          {t('public.viewAvailable')}
        </Link>
      </div>
    </PageShell>
  );
}

// CHECKOUT PAGE
export function CheckoutPage() {
  const { t } = useTranslation();
  return (
    <PageShell>
      <div className="max-w-sm mx-auto px-6 py-16 space-y-8 text-center">
        <ContentRenderer blockKey="checkout_title" as="h1" className="font-serif text-3xl tracking-widest" />
        <p className="text-sm font-sans text-charcoal/40">{t('checkout.integrationPending')}</p>
        <Link to="/house" className="inline-block px-8 py-3 border border-charcoal/10 text-[11px] tracking-widest uppercase font-sans text-charcoal/50 hover:text-charcoal transition-colors">
          {t('public.returnToHouse')}
        </Link>
      </div>
    </PageShell>
  );
}
